<template>
  <div>
    <form-gcr
      data-test-id="form"
      ref="form"
      :title="$t('inspectionRequest')"
      :cancel-url="projectDetailUrl"
      @submit.prevent="confirmerRDV"
    >
      <template #buttons>
        <button-gcr
          data-test-id="submit-button"
          btn-class="btn-primary btn-sm form__header__btn"
          label="confirmAppointment"
          type="button"
          :disabled="isReadOnly || !selectedType || !selectedDate || !selectedTime"
          :loading="submitting"
          @btnclick="confirmerRDV"
        />
      </template>

      <div class="form__body row">
        <div class="form__container col-xs-10 col-sm-8 col-md-6 offset-xs-1 offset-md-2">
          <div class="row">
            <div class="col form__col">
              <h2 data-test-id="building-address">{{ ProjectName }}</h2>

              <ul>
                <li v-for="building in selectedBuildings" :key="building.id">
                  {{ building.formattedInfo }}
                </li>
              </ul>

              <div data-test-id="info-line" v-html="$t('inspectionRequestInfo')"></div>
            </div>
          </div>

          <div class="row">
            <div class="form__col col col-xs-6">
              <label for="type-rdv">{{ $t('appointmentType') }}</label>
              <select-gcr data-test-id="appointment-type" :options="appointmentTypeOptions" v-model="selectedType" />
            </div>
          </div>

          <div class="row">
            <div class="form__col col col-xs-6">
              <label>{{ $t('date') }}</label>
              <date-gcr
                data-test-id="datepicker"
                v-model="selectedDate"
                :placeholder="$t('chooseDate')"
                locale="fr"
                :disabledDates="disabledDatesFn"
                class="datepicker"
              />
            </div>
            <div class="form__col col col-xs-6">
              <label>{{ $t('time') }}</label>
              <select-gcr
                data-test-id="appointment-time"
                :options="appointmentTimeTypeOptions"
                :placeholder="$t('chooseTime')"
                v-model="selectedTime"
              />
            </div>
          </div>

          <div
            class="important-note"
            data-test-id="inspection-request-important-note"
            v-html="$t('inspectionRequestImportantNote')"
          ></div>
        </div>
      </div>
    </form-gcr>
  </div>
</template>

<script>
import getProjectDetails from '@/graphql/accreditations/projects/getProjectDetails.graphql'
import submitInspectionMutation from '@/graphql/inspections/submitInspection.graphql'
import SelectGcr from '@/components/atoms/Select'
import DateGcr from '@/components/atoms/Date'
import ButtonGcr from '@/components/atoms/Button'
import FormGcr from '@/components/molecules/Form'
import { Consts, Urls, Dates } from '@/_helpers'
import { mapGetters } from 'vuex'
import alerts from '@/_helpers/alerts'
import form from '@/components/mixins/form'
import { buildingDescription } from '@/_helpers/buildings'
import { shortAddress } from '@/_helpers/address'
import '@/utilities/noteImportante/important-note.scss'
import { DateTime } from 'luxon'
import appointmentTimeTypes from '@/_models/appointment_time_types'

export default {
  components: { FormGcr, SelectGcr, DateGcr, ButtonGcr },
  mixins: [form],
  props: {
    projectId: String,
    buildingId: String
  },
  data() {
    let buildingIds = []
    if (this.$route.query.buildingIds) {
      buildingIds = this.$route.query.buildingIds.split(',')
    }
    return {
      selectedType: null,
      selectedDate: null,
      selectedTime: null,
      submitting: false,
      projectDetailUrl: {
        name: Urls.PROJECT_DETAILS,
        query: {
          id: this.projectId
        }
      },
      ProjectName: null,
      buildings: [],
      computedBuildingIds: buildingIds.length > 0 ? buildingIds : [this.buildingId]
    }
  },
  computed: {
    ...mapGetters({
      accreditationId: Consts.GETTER_ACCREDITATION_CURRENT_ID,
      isReadOnly: Consts.GETTER_USER_ISSAC
    }),
    appointmentTypeOptions() {
      return [{ text: this.$t('appointmentTypeOption'), value: 'INSIDE_WALL_COVERINGS' }]
    },
    appointmentTimeTypeOptions() {
      return appointmentTimeTypes.map((x) => ({ value: x.submitValue, text: x.text }))
    },
    disabledDatesFn() {
      const todayInCanada = DateTime.now().setZone('America/Toronto').startOf('day')

      return {
        days: [6, 0],
        to: Dates.addBusinessDays(todayInCanada, 5)
      }
    },
    selectedBuildings() {
      return this.buildings
        .filter((building) => this.computedBuildingIds.includes(building.id))
        .map((building) => ({
          id: building.id,
          formattedInfo: `${shortAddress(building)} - ${buildingDescription(building)} - ${building.lotNumber}`
        }))
    }
  },
  apollo: {
    getProjectDetails: {
      query: getProjectDetails,
      variables() {
        return { accreditationId: this.accreditationId, projectId: this.projectId }
      },
      skip() {
        return !this.projectId
      },
      update({ accreditation }) {
        if (accreditation && accreditation.project) {
          this.ProjectName = accreditation.project.name
          this.buildings = accreditation.project.buildings || []
        }
      }
    }
  },
  methods: {
    async confirmerRDV() {
      if (!this.selectedType || !this.selectedDate || !this.selectedTime) {
        alerts.showError(this.$t('missingRquiredFields'))
        return
      }

      const formattedDate = this.selectedDate instanceof Date ? this.selectedDate.toISOString().split('T')[0] : this.selectedDate

      this.submitting = true
      try {
        const response = await this.$apollo.mutate({
          mutation: submitInspectionMutation,
          variables: {
            input: {
              accreditationId: this.accreditationId,
              buildingsId: this.computedBuildingIds,
              desiredDate: formattedDate,
              desiredTime: this.selectedTime,
              projectId: this.projectId,
              type: this.selectedType
            }
          }
        })

        const errors = response.data.submitInspection.submittedInspection.errors
        if (errors.length > 0) {
          errors.forEach((error) => alerts.showError(error.message))
        } else {
          alerts.showInfo(this.$t('appointmentConfirmed'))
          this.$router.push(this.projectDetailUrl)
        }
      } catch (error) {
        alerts.showError(this.$t('appointmentError'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
